import React, { type ReactNode, useCallback, useContext, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { Spotlight, SpotlightTransition } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';

import { useEditionAwareness } from '../services/edition-awareness-context';

type ActiveSpotlightId = 'standard-to-premium';

const SpotlightContext = React.createContext<((id: ActiveSpotlightId) => void) | null>(null);

export function useSpotlight() {
	const setActiveSpotlight = useContext(SpotlightContext);

	if (!setActiveSpotlight) {
		if (process.env.NODE_ENV !== 'production' && !process.env.CI) {
			throw new Error('useSpotlight needs to be used within the SpotlightContext');
		}

		return { setActiveSpotlight: () => {} };
	}

	return { setActiveSpotlight };
}

function GotIt() {
	return (
		<FormattedMessage
			id="@atlassian/edition-awareness.spotlights.got-it"
			defaultMessage="Got it"
			description="Spotlight button text which is used to dismiss the spotlight"
		/>
	);
}

function ActiveSpotlight({ id, onDismiss }: { id: ActiveSpotlightId; onDismiss: () => void }) {
	const { product } = useEditionAwareness();

	if (id === 'standard-to-premium') {
		const target = product === 'confluence' ? 'nav-profile' : 'profile-spotlight';

		return (
			<SpotlightTransition>
				<Spotlight
					actions={[
						{
							onClick: onDismiss,
							text: <GotIt />,
						},
					]}
					target={target}
					targetRadius={3}
					dialogWidth={275}
					targetBgColor={token('utility.elevation.surface.current')}
				>
					<FormattedMessage
						id="@atlassian/edition-awareness.spotlight.message"
						defaultMessage="Not ready to upgrade? Return to your profile when you're ready."
						description="Message to show in the spotlight on the profile menu icon when the user dismisses the EA pill and is eligible for standard to premium upsell"
					/>
				</Spotlight>
			</SpotlightTransition>
		);
	}

	return null;
}

export function Spotlights({ children }: { children: ReactNode }) {
	const [activeSpotlight, setActiveSpotlight] = useState<ActiveSpotlightId | null>(null);
	const handleOnDismiss = useCallback(() => setActiveSpotlight(null), []);

	return (
		<>
			<SpotlightContext.Provider value={setActiveSpotlight}>{children}</SpotlightContext.Provider>
			{activeSpotlight && <ActiveSpotlight id={activeSpotlight} onDismiss={handleOnDismiss} />}
		</>
	);
}
